import { createContext, useContext, useEffect, useMemo, useState } from "react";

import type { Agent } from "@/types/agents";
import { createAgent, getAgents } from "@/apis/agents";

import { useProject } from "./project-provider";
import { useUser } from "./user-provider";

type Props = {
  children: React.ReactNode;
};

type State = {
  agent: Agent | null;
  loading: boolean;
};

const initialState: State = {
  agent: null,
  loading: true,
};

const AgentProviderContext = createContext<State>(initialState);

export const AgentProvider: React.FC<Props> = ({ children }) => {
  const { login, getAccessToken } = useUser();
  const { project, loading: projectLoading } = useProject();
  const [agent, setAgent] = useState<Agent | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const projectId = project?.id;

    if (!projectId || projectLoading) {
      return;
    }

    /**
     * A late response from the previous project must not overwrite the
     * current agent — nothing would fetch again to fix it.
     */
    let ignore = false;

    const ensureAgent = async () => {
      setAgent(null);
      setLoading(true);
      try {
        const token = await getAccessToken();
        const agents = await getAgents(projectId, token ?? "", login);

        const existingAgent =
          agents?.find(
            ({ medplum_project_id: medplumProjectId }) =>
              medplumProjectId === projectId
          ) ?? null;

        if (existingAgent) {
          if (!ignore) {
            setAgent(existingAgent);
          }
        } else {
          const created = await createAgent(
            {
              medplum_project_id: projectId,
            },
            token ?? "",
            login
          );
          if (!ignore) {
            setAgent(created ?? null);
          }
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    void ensureAgent();

    return () => {
      ignore = true;
    };
  }, [project?.id, projectLoading, getAccessToken, login]);

  const value = useMemo(() => ({ agent, loading }), [agent, loading]);

  return (
    <AgentProviderContext.Provider value={value}>
      {children}
    </AgentProviderContext.Provider>
  );
};

export const useAgent = () => {
  const context = useContext(AgentProviderContext);

  if (context === undefined) {
    throw new Error("useAgent must be used within an AgentProvider");
  }

  return context;
};
